import { useState, useEffect } from 'react';
import { api } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function AdminLegalPage({ user, onLogout }) {
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('terms'); // 'terms' or 'privacy'

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const [termsRes, privacyRes] = await Promise.all([
        api.get('/legal/terms'),
        api.get('/legal/privacy')
      ]);
      setTermsContent(termsRes.data.content || '');
      setPrivacyContent(privacyRes.data.content || '');
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (type) => {
    setSaving(true);
    try {
      const content = type === 'terms' ? termsContent : privacyContent;
      await api.put(`/legal/${type}`, { content });
      toast.success(`${type === 'terms' ? 'Terms and Conditions' : 'Privacy Policy'} updated successfully!`);
    } catch (error) {
      toast.error('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Manage Legal Documents</h1>
          
          {/* Tabs */}
          <div className="flex gap-4 mb-6 border-b">
            <button
              onClick={() => setActiveTab('terms')}
              className={`px-6 py-3 font-semibold ${
                activeTab === 'terms'
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Terms and Conditions
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`px-6 py-3 font-semibold ${
                activeTab === 'privacy'
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Privacy Policy
            </button>
          </div>

          {/* Terms Editor */}
          {activeTab === 'terms' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="terms-editor">Terms and Conditions Content</Label>
                <p className="text-sm text-gray-500 mb-2">Use Markdown for formatting</p>
                <Textarea
                  id="terms-editor"
                  value={termsContent}
                  onChange={(e) => setTermsContent(e.target.value)}
                  rows={20}
                  className="font-mono text-sm"
                  placeholder="# Terms and Conditions&#10;&#10;## 1. Introduction&#10;..."
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleSave('terms')}
                  disabled={saving}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white"
                >
                  {saving ? 'Saving...' : 'Save Terms'}
                </Button>
                <Button
                  onClick={() => window.open('/terms', '_blank')}
                  variant="outline"
                >
                  Preview
                </Button>
              </div>
            </div>
          )}

          {/* Privacy Editor */}
          {activeTab === 'privacy' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="privacy-editor">Privacy Policy Content</Label>
                <p className="text-sm text-gray-500 mb-2">Use Markdown for formatting</p>
                <Textarea
                  id="privacy-editor"
                  value={privacyContent}
                  onChange={(e) => setPrivacyContent(e.target.value)}
                  rows={20}
                  className="font-mono text-sm"
                  placeholder="# Privacy Policy&#10;&#10;## 1. Data Collection&#10;..."
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleSave('privacy')}
                  disabled={saving}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white"
                >
                  {saving ? 'Saving...' : 'Save Privacy Policy'}
                </Button>
                <Button
                  onClick={() => window.open('/privacy', '_blank')}
                  variant="outline"
                >
                  Preview
                </Button>
              </div>
            </div>
          )}

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Markdown Formatting Guide:</strong>
            </p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• <code className="bg-white px-2 py-1 rounded"># Heading 1</code> - Main title (large)</li>
              <li>• <code className="bg-white px-2 py-1 rounded">## Heading 2</code> - Section title</li>
              <li>• <code className="bg-white px-2 py-1 rounded">### Heading 3</code> - Subsection title</li>
              <li>• <code className="bg-white px-2 py-1 rounded">**bold text**</code> - Makes text bold and orange</li>
              <li>• <code className="bg-white px-2 py-1 rounded">- List item</code> - Creates bullet points</li>
              <li>• <code className="bg-white px-2 py-1 rounded">---</code> - Creates a horizontal line</li>
              <li>• Leave <strong>blank lines</strong> between paragraphs for proper spacing</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
